const ERROR_CODES = [409, 429, 500, 502, 503, 504];
const RETRIES = 3;
const BACKOFF = 300;

export class HttpResponseError extends Error {
  status: number;

  constructor(status: number) {
    super(`HTTP error! Status: ${status}`);
    this.name = 'HttpResponseError';
    this.status = status;
  }
}

interface Params {
  url: string;
  options: RequestInit;
  retries?: number;
  backoff?: number;
}

interface ResponseParams {
  input: RequestInfo | URL;
  init?: RequestInit;
  retries?: number;
  backoff?: number;
}

const waitForRetry = (
  delay: number,
  signal?: AbortSignal | null
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    const handleAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };
    timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delay);

    signal?.addEventListener('abort', handleAbort, { once: true });
  });

export const fetchResponseWithRetry = async ({
  input,
  init,
  retries = RETRIES,
  backoff = BACKOFF,
}: ResponseParams): Promise<Response> => {
  try {
    // A Request body is single-use. Keep the original untouched and clone it
    // for each attempt; URL + RequestInit callers can reuse their string body.
    const attemptInput = input instanceof Request ? input.clone() : input;
    const response = await fetch(attemptInput, init);

    if (!response.ok) {
      if (retries > 0 && ERROR_CODES.includes(response.status)) {
        const signal =
          init?.signal ?? (input instanceof Request ? input.signal : undefined);
        await waitForRetry(backoff, signal);

        return await fetchResponseWithRetry({
          input,
          init,
          retries: retries - 1,
          backoff: backoff * 2,
        });
      }
      throw new HttpResponseError(response.status);
    }

    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Request failed: ${String(input)}`, error);
    throw error;
  }
};

export const fetchWithRetry = async <T>({
  url,
  options,
  retries,
  backoff,
}: Params): Promise<T | null> => {
  const response = await fetchResponseWithRetry({
    input: url,
    init: options,
    retries,
    backoff,
  });

  if (response.status === 204) {
    return null;
  }

  return response.json();
};
